import { cn } from '@/lib/utils/helpers'
import type { ResidentListLabelMaps } from '../types'
import { lookupConstantLabel, normalizeConstantKey } from './residentListConstants'
import { RENT_PAID_BADGE_BASE, RENT_PAID_BADGE_TONES } from '../constants/listBadgeStyles'

/**
 * Rent Paid Status badge classes — matches listing screenshot:
 * Paid in Full (green), Not Paid in Full (amber), Not updated (grey).
 */
export function rentPaidStatusBadgeClass(payStatus?: string | null): string {
  const key = normalizeConstantKey(payStatus)
  if (key === 'PAID') {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.paid,
    )
  }
  if (key === 'NOT_PAID') {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.notPaid,
    )
  }
  if (!key) {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.notUpdated,
    )
  }
  return cn(
    RENT_PAID_BADGE_BASE,
    RENT_PAID_BADGE_TONES.neutral,
  )
}

export function rentPaidStatusLabel(
  payStatus?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const key = normalizeConstantKey(payStatus)
  if (!key) return 'Not updated'
  return lookupConstantLabel(labelMaps?.pay_status, payStatus)
}

/**
 * Normalize rental status tokens from the list API (e.g. `Active`, `ACTIVE`, `Not Started`).
 */
export function normalizeRentalStatusKey(rentalStatus?: string | null): string {
  const raw = String(rentalStatus || '').trim()
  if (!raw) return 'NOT_STARTED'
  const upper = raw.toUpperCase().replace(/\s+/g, '_')
  if (upper === 'NOT_STARTED' || upper === 'NOTSTARTED') return 'NOT_STARTED'
  if (upper === 'ACTIVE') return 'ACTIVE'
  if (upper === 'CLOSED' || upper === 'CLOSE') return 'CLOSED'
  return upper
}

/**
 * Rental Status text color — matches listing screenshot rules:
 * Not Started (grey), Active (green), Skip / Eviction / Close (red).
 */
export function rentalStatusTextClass(rentalStatus?: string | null): string {
  const key = normalizeRentalStatusKey(rentalStatus)
  if (key === 'ACTIVE') return 'font-medium text-[#1e7e34]'
  if (key === 'CLOSED') return 'font-medium text-[#c62828]'
  return 'font-medium text-[#5f6368]'
}

export function rentalStatusLabel(
  rentalStatus?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const key = normalizeRentalStatusKey(rentalStatus)
  return (
    lookupConstantLabel(labelMaps?.resident_rental_status, key) ||
    lookupConstantLabel(labelMaps?.resident_rental_status, rentalStatus) ||
    String(rentalStatus || 'Not Started')
  )
}

/** Display label for list row `resident_rental_status`. */
export const residentRentalStatusLabel = rentalStatusLabel

/** Display class for list row `resident_rental_status`. */
export const residentRentalStatusTextClass = rentalStatusTextClass

/** Whether a list row is selectable (bulk paid / multi-edit). */
export function isActiveResidentRentalStatus(status?: string | null): boolean {
  return normalizeRentalStatusKey(status) === 'ACTIVE'
}

export function residentRentalProgressLabel(
  progress?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const raw = String(progress || '').trim()
  if (!raw) return ''
  const key = normalizeConstantKey(raw)
  return (
    lookupConstantLabel(labelMaps?.resident_rental_progress, key) ||
    lookupConstantLabel(labelMaps?.resident_rental_progress, raw) ||
    raw
  )
}

export function reimbursementTypeLabel(
  reimbursementType?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const raw = String(reimbursementType || '').trim()
  if (!raw) return ''
  const key = normalizeConstantKey(raw)
  return (
    lookupConstantLabel(labelMaps?.reimbursement_type, key) ||
    lookupConstantLabel(labelMaps?.reimbursement_type, raw) ||
    raw
  )
}

export function reimbursementTypeTextClass(reimbursementType?: string | null): string {
  if (!String(reimbursementType || '').trim()) return 'font-medium text-[#5f6368]'
  return 'font-medium text-[#c62828]'
}

export function reimbursementStatusLabel(
  reimbursementStatus?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const raw = String(reimbursementStatus || '').trim()
  if (!raw) return ''
  const key = normalizeConstantKey(raw)
  return (
    lookupConstantLabel(labelMaps?.reimbursement_rental_status, key) ||
    lookupConstantLabel(labelMaps?.reimbursement_rental_status, raw) ||
    raw
  )
}

export function reimbursementStatusTextClass(reimbursementStatus?: string | null): string {
  const key = normalizeConstantKey(reimbursementStatus)
  if (key === 'OPEN') return 'font-medium text-[#1e7e34]'
  if (key === 'SUBMITTED') return 'font-medium text-[#e65100]'
  return 'font-medium text-[#5f6368]'
}

export function reimbursementRentalProgressLabel(
  progress?: string | null,
  labelMaps?: ResidentListLabelMaps,
): string {
  const raw = String(progress || '').trim()
  if (!raw) return ''
  const key = normalizeConstantKey(raw)
  return (
    lookupConstantLabel(labelMaps?.reimbursement_rental_progress, key) ||
    lookupConstantLabel(labelMaps?.reimbursement_rental_progress, raw) ||
    raw
  )
}

export function reimbursementRentalProgressTextClass(progress?: string | null): string {
  const key = normalizeConstantKey(progress)
  if (key === 'PAID') return 'font-medium text-[#1e7e34]'
  if (key === 'DENIED' || key === 'CANCELLED' || key === 'EXPIRED') {
    return 'font-medium text-[#c62828]'
  }
  if (key === 'IN_REVIEW' || key === 'READY_FOR_REVIEW') {
    return 'font-medium text-[#e65100]'
  }
  return 'font-medium text-[#5f6368]'
}
